import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError, openApiFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Archive, Download, Play, ShieldCheck, AlertTriangle, FileArchive, RefreshCw } from "lucide-react";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];


function formatSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}


export default function AdminSriBackup() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const [year, setYear] = useState(prev.getFullYear());
  const [month, setMonth] = useState(prev.getMonth() + 1);
  const [generating, setGenerating] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sri-backup");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const generar = async (force = false) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/sri-backup/generar",
        { year: Number(year), month: Number(month), force });
      if (data.skipped && data.total_docs === 0) {
        toast.info(`${year}-${String(month).padStart(2, "0")}: no hay XMLs autorizados en este mes`);
      } else if (data.skipped) {
        toast.info(`Respaldo ya existía. ${formatSize(data.size_bytes)}`);
      } else {
        toast.success(`Respaldo generado: ${data.total_docs} XMLs · ${formatSize(data.size_bytes)}`);
      }
      cargar();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGenerating(false); }
  };

  const descargar = (y, m) => {
    openApiFile(`/admin/sri-backup/descargar/${y}/${m}`, {
      filename: `sri_xml_${y}-${String(m).padStart(2, "0")}.zip`,
      mime: "application/zip",
      downloadOnly: true,
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const yearsList = [];
  for (let y = today.getFullYear(); y >= 2024; y--) yearsList.push(y);

  return (
    <div className="space-y-4" data-testid="admin-sri-backup-page">
      <div>
        <h1 className="text-2xl font-bold text-[#15241d] flex items-center gap-2">
          <Archive size={22} className="text-[#205c45]" />
          Respaldos SRI (XMLs autorizados)
        </h1>
        <p className="text-sm text-[#5c7368] mt-0.5">
          Conservación legal de comprobantes electrónicos. Por ley en Ecuador debes mantener los XML autorizados por <b>7 años</b>.
          El respaldo automático corre entre el día 5 y 10 de cada mes.
        </p>
      </div>

      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 mb-2 text-[#205c45]">
          <Play size={16} />
          <h2 className="font-semibold text-sm">Generar respaldo manualmente</h2>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Año</Label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                    className="h-9 px-2 border border-[#d1dbd6] rounded text-sm" data-testid="backup-year">
              {yearsList.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Mes</Label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
                    className="h-9 px-2 border border-[#d1dbd6] rounded text-sm" data-testid="backup-month">
              {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <Button onClick={() => generar(false)} disabled={generating} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="backup-generar-btn">
            <Play size={14} className="mr-1.5" />
            {generating ? "Generando..." : "Generar respaldo"}
          </Button>
          <Button variant="outline" onClick={() => generar(true)} disabled={generating} data-testid="backup-regenerar-btn">
            <RefreshCw size={14} className="mr-1.5" />
            Regenerar (forzar)
          </Button>
        </div>
        <div className="text-[11px] text-[#8ba397] flex items-start gap-1.5 mt-2">
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <span>"Regenerar" sobrescribe el ZIP existente. Útil si añadiste XMLs autorizados con fecha retroactiva.</span>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[#205c45]">
            <FileArchive size={16} />
            <h2 className="font-semibold text-sm">Respaldos disponibles</h2>
            <Badge className="bg-[#e9f2ee] text-[#205c45]">{items.length}</Badge>
          </div>
          <Button size="sm" variant="outline" onClick={cargar} disabled={loading}>
            <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
            Recargar
          </Button>
        </div>

        <div className="border border-[#e6ecea] rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf9] text-left text-[#5c7368]">
              <tr>
                <th className="p-2">Período</th>
                <th className="p-2 text-right">XMLs</th>
                <th className="p-2 text-right">Tamaño</th>
                <th className="p-2">Generado</th>
                <th className="p-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-[#8ba397]">
                  Aún no hay respaldos. Genera el primero con el formulario superior.
                </td></tr>
              )}
              {items.map((r) => (
                <tr key={r._id} className="border-t border-[#e6ecea] hover:bg-[#f7fbf9]" data-testid={`backup-row-${r._id}`}>
                  <td className="p-2 font-mono text-[#15241d] flex items-center gap-1.5">
                    <ShieldCheck size={12} className="text-emerald-600" />
                    {MESES[r.month - 1]} {r.year}
                  </td>
                  <td className="p-2 text-right">{r.total_docs?.toLocaleString() || 0}</td>
                  <td className="p-2 text-right">{formatSize(r.size_bytes)}</td>
                  <td className="p-2 text-[11px] text-[#5c7368]">
                    {(r.generated_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => descargar(r.year, r.month)} data-testid={`backup-dl-${r._id}`}>
                      <Download size={12} className="mr-1" />
                      Descargar ZIP
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
